import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import { formatDateDDMMYYYY } from "../../../utils/formatDate";
import "./styles/ServiceDateTimeRangeFilter.css";

export type DateTimeRangeValue = {
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
};

const EMPTY: DateTimeRangeValue = {
  fromDate: "",
  fromTime: "",
  toDate: "",
  toTime: "",
};

type Props = {
  value: DateTimeRangeValue;
  onChange: (value: DateTimeRangeValue) => void;
  label?: string;
};

const WEEKDAY_LABELS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sa", "Do"];
const MONTH_LABELS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function toYmd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function toHm(d: Date): string {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function parseYmd(s: string): Date | null {
  if (!s) return null;
  const d = new Date(`${s}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 0, 0);
  return x;
}

function startOfWeek(d: Date): Date {
  const x = startOfDay(d);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}

function endOfWeek(d: Date): Date {
  const x = startOfWeek(d);
  x.setDate(x.getDate() + 6);
  return endOfDay(x);
}

function rangeToValue(from: Date, to: Date): DateTimeRangeValue {
  return {
    fromDate: toYmd(from),
    fromTime: toHm(from),
    toDate: toYmd(to),
    toTime: toHm(to),
  };
}

type PresetId = "today" | "next24h" | "thisWeek" | "next7d";

const PRESETS: Array<{ id: PresetId; label: string; build: () => DateTimeRangeValue }> = [
  {
    id: "today",
    label: "Hoy",
    build: () => {
      const now = new Date();
      return rangeToValue(startOfDay(now), endOfDay(now));
    },
  },
  {
    id: "next24h",
    label: "Próximas 24 h",
    build: () => {
      const now = new Date();
      const end = new Date(now.getTime() + 24 * 60 * 60 * 1000);
      return rangeToValue(now, end);
    },
  },
  {
    id: "thisWeek",
    label: "Esta semana",
    build: () => {
      const now = new Date();
      return rangeToValue(startOfWeek(now), endOfWeek(now));
    },
  },
  {
    id: "next7d",
    label: "Próximos 7 días",
    build: () => {
      const now = new Date();
      const end = new Date(now);
      end.setDate(end.getDate() + 7);
      return rangeToValue(startOfDay(now), endOfDay(end));
    },
  },
];

function formatRangeSummary(value: DateTimeRangeValue): string {
  const { fromDate, fromTime, toDate, toTime } = value;
  if (!fromDate && !toDate && !fromTime && !toTime) return "";
  const fromParts = [formatDateDDMMYYYY(fromDate), fromTime].filter(Boolean).join(" ");
  const toParts = [formatDateDDMMYYYY(toDate), toTime].filter(Boolean).join(" ");
  if (fromParts && toParts) return `${fromParts} — ${toParts}`;
  return fromParts || toParts;
}

function compareYmd(a: string, b: string): number {
  if (!a || !b) return 0;
  return a.localeCompare(b);
}

function buildCalendarDays(year: number, month: number): Array<{ date: string; day: number; inMonth: boolean }> {
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - startOffset);
  const days: Array<{ date: string; day: number; inMonth: boolean }> = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push({
      date: toYmd(d),
      day: d.getDate(),
      inMonth: d.getMonth() === month,
    });
  }
  return days;
}

const ServiceDateTimeRangeFilter: React.FC<Props> = ({
  value,
  onChange,
  label = "Fecha y hora",
}) => {
  const labelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<DateTimeRangeValue>(value);
  const [viewDate, setViewDate] = useState(() => parseYmd(value.fromDate) ?? new Date());
  const [pickPhase, setPickPhase] = useState<"start" | "end">("start");
  const [focusedDay, setFocusedDay] = useState<string | null>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

  const hasValue = Boolean(
    value.fromDate || value.fromTime || value.toDate || value.toTime
  );
  const summary = formatRangeSummary(value);

  const syncDraftFromValue = useCallback(() => {
    setDraft(value);
    const anchor = parseYmd(value.fromDate) ?? parseYmd(value.toDate) ?? new Date();
    setViewDate(anchor);
    setPickPhase("start");
  }, [value]);

  const positionPanel = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    const panelWidth = Math.min(440, window.innerWidth - margin * 2);
    const left = Math.min(
      Math.max(margin, r.left),
      window.innerWidth - panelWidth - margin
    );
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const preferBelow = spaceBelow >= 280;
    setPanelStyle({
      position: "fixed",
      left,
      width: panelWidth,
      zIndex: 15000,
      ...(preferBelow
        ? { top: r.bottom + 6 }
        : { bottom: window.innerHeight - r.top + 6 }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    positionPanel();
    const onReflow = () => positionPanel();
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [isOpen, positionPanel]);

  useEffect(() => {
    if (isOpen) syncDraftFromValue();
  }, [isOpen, syncDraftFromValue]);

  useEffect(() => {
    if (!isOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen]);

  const calendarDays = useMemo(
    () => buildCalendarDays(viewDate.getFullYear(), viewDate.getMonth()),
    [viewDate]
  );

  const commitValue = (next: DateTimeRangeValue) => {
    onChange(next);
    setDraft(next);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const applyDraft = () => {
    const next = { ...draft };
    if (next.fromDate && !next.toDate) {
      next.toDate = next.fromDate;
      next.toTime = next.toTime || next.fromTime || "23:59";
    }
    if (next.fromDate && !next.fromTime) next.fromTime = "00:00";
    if (next.toDate && !next.toTime) next.toTime = "23:59";
    commitValue(next);
  };

  const clearAll = () => {
    commitValue(EMPTY);
  };

  const applyPreset = (preset: (typeof PRESETS)[number]) => {
    commitValue(preset.build());
  };

  const handleDaySelect = (date: string) => {
    setFocusedDay(date);

    if (pickPhase === "start") {
      const next: DateTimeRangeValue = {
        fromDate: date,
        fromTime: draft.fromTime || "00:00",
        toDate: "",
        toTime: "23:59",
      };
      setDraft(next);
      setPickPhase("end");
      return;
    }

    let from = draft.fromDate;
    let to = date;
    if (from && compareYmd(date, from) < 0) {
      to = from;
      from = date;
    }

    const next: DateTimeRangeValue = {
      fromDate: from,
      fromTime: draft.fromTime || "00:00",
      toDate: to,
      toTime: draft.toTime || "23:59",
    };
    setDraft(next);
    setPickPhase("start");
    commitValue(next);
  };

  const handleDayKeyDown = (e: React.KeyboardEvent, date: string, index: number) => {
    const move = (delta: number) => {
      const next = calendarDays[Math.max(0, Math.min(calendarDays.length - 1, index + delta))];
      if (next) {
        setFocusedDay(next.date);
        panelRef.current
          ?.querySelector<HTMLButtonElement>(`[data-cal-date="${next.date}"]`)
          ?.focus();
      }
    };

    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        handleDaySelect(date);
        break;
      case "ArrowLeft":
        e.preventDefault();
        move(-1);
        break;
      case "ArrowRight":
        e.preventDefault();
        move(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        move(-7);
        break;
      case "ArrowDown":
        e.preventDefault();
        move(7);
        break;
      default:
        break;
    }
  };

  const isInRange = (date: string) => {
    const { fromDate, toDate } = draft;
    if (!fromDate) return false;
    if (!toDate) return date === fromDate;
    return compareYmd(date, fromDate) >= 0 && compareYmd(date, toDate) <= 0;
  };

  const isRangeStart = (date: string) => draft.fromDate === date;
  const isRangeEnd = (date: string) => draft.toDate === date;

  const panel = isOpen ? (
    <div
      ref={panelRef}
      className="svc-dt-range__panel svc-dt-range__panel--portal"
      style={panelStyle}
      role="dialog"
      aria-label="Selector de rango de fecha y hora"
    >
      <div className="svc-dt-range__presets" role="listbox" aria-label="Rangos rápidos">
        {PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            role="option"
            className="svc-dt-range__preset"
            onClick={() => applyPreset(preset)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="svc-dt-range__calendar-wrap">
        <p className="svc-dt-range__hint">
          {pickPhase === "start"
            ? "Selecciona la fecha de inicio"
            : "Selecciona la fecha de fin"}
        </p>

        <div className="svc-dt-range__cal-header">
          <button
            type="button"
            className="svc-dt-range__nav"
            aria-label="Mes anterior"
            onClick={() =>
              setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))
            }
          >
            <ChevronLeft size={16} />
          </button>
          <span className="svc-dt-range__cal-title">
            {MONTH_LABELS[viewDate.getMonth()]} {viewDate.getFullYear()}
          </span>
          <button
            type="button"
            className="svc-dt-range__nav"
            aria-label="Mes siguiente"
            onClick={() =>
              setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))
            }
          >
            <ChevronRight size={16} />
          </button>
        </div>

        <div className="svc-dt-range__weekdays" aria-hidden>
          {WEEKDAY_LABELS.map((w) => (
            <span key={w} className="svc-dt-range__weekday">
              {w}
            </span>
          ))}
        </div>

        <div className="svc-dt-range__grid" role="grid" aria-label="Calendario">
          {calendarDays.map((cell, index) => {
            const selected =
              isRangeStart(cell.date) || isRangeEnd(cell.date) || isInRange(cell.date);
            return (
              <button
                key={`${cell.date}-${index}`}
                type="button"
                role="gridcell"
                data-cal-date={cell.date}
                tabIndex={
                  focusedDay === cell.date || (!focusedDay && cell.inMonth && index === 0)
                    ? 0
                    : -1
                }
                className={[
                  "svc-dt-range__day",
                  !cell.inMonth ? "is-outside" : "",
                  selected ? "is-selected" : "",
                  isRangeStart(cell.date) ? "is-start" : "",
                  isRangeEnd(cell.date) ? "is-end" : "",
                  isInRange(cell.date) ? "is-in-range" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleDaySelect(cell.date)}
                onKeyDown={(e) => handleDayKeyDown(e, cell.date, index)}
                onFocus={() => setFocusedDay(cell.date)}
                aria-label={formatDateDDMMYYYY(cell.date)}
                aria-selected={selected}
              >
                {cell.day}
              </button>
            );
          })}
        </div>

        <div className="svc-dt-range__times">
          <label className="svc-dt-range__time-label">
            Desde
            <input
              type="time"
              className="svc-dt-range__time-input"
              value={draft.fromTime}
              onChange={(e) => setDraft((p) => ({ ...p, fromTime: e.target.value }))}
              aria-label="Hora desde"
            />
          </label>
          <label className="svc-dt-range__time-label">
            Hasta
            <input
              type="time"
              className="svc-dt-range__time-input"
              value={draft.toTime}
              onChange={(e) => setDraft((p) => ({ ...p, toTime: e.target.value }))}
              aria-label="Hora hasta"
            />
          </label>
        </div>
      </div>

      <div className="svc-dt-range__footer">
        <button type="button" className="svc-dt-range__btn svc-dt-range__btn--ghost" onClick={clearAll}>
          Limpiar
        </button>
        <button type="button" className="svc-dt-range__btn svc-dt-range__btn--primary" onClick={applyDraft}>
          Aplicar
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={`svc-dt-range${hasValue ? " svc-dt-range--active" : ""}${isOpen ? " svc-dt-range--open" : ""}`}
    >
      <label className="filter-label-pro svc-dt-range__label" id={labelId}>
        <Calendar size={14} aria-hidden />
        {label}
      </label>

      <div className="svc-dt-range__trigger-wrap">
        <button
          ref={triggerRef}
          type="button"
          className="svc-dt-range__trigger"
          aria-labelledby={labelId}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((v) => !v)}
        >
          <Calendar size={18} className="svc-dt-range__trigger-icon" aria-hidden />
          <span className="svc-dt-range__trigger-text">
            {summary || "Seleccionar rango de fecha y hora"}
          </span>
        </button>
        {hasValue && (
          <button
            type="button"
            className="svc-dt-range__trigger-clear"
            aria-label="Limpiar rango de fecha y hora"
            onClick={clearAll}
          >
            <X size={14} aria-hidden />
          </button>
        )}
      </div>

      {panel && createPortal(panel, document.body)}
    </div>
  );
};

export default ServiceDateTimeRangeFilter;
